import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Copy, Trash2, RefreshCw, LogOut, Loader2, Check } from "lucide-react";
import { usePremium } from "../context/PremiumContext";
import { getFamily, joinFamily, removeFamilyMember, leaveFamily, regenerateFamilyCode } from "../lib/api";
import { isNativeIOS } from "../lib/native";
import { track } from "../lib/analytics";

const deviceLabel = () => {
  const ua = navigator.userAgent || "";
  const hit = ["iPhone", "iPad", "Android", "Macintosh", "Windows", "CrOS", "Linux"].find((k) => ua.includes(k));
  return { Macintosh: "Mac", CrOS: "Chromebook" }[hit] || hit || "Family device";
};

const errText = (e) => e?.response?.data?.detail || "Something went wrong. Try again.";

const OwnerView = ({ fam, did, onChange }) => {
  const [copied, setCopied] = useState(null);
  const [err, setErr] = useState(null);
  const copy = async () => {
    try { await navigator.clipboard.writeText(fam.code); setCopied("Copied"); }
    catch { setCopied("Copy it by hand"); }
    setTimeout(() => setCopied(null), 2200);
  };
  const act = (fn) => { setErr(null); fn().then(onChange).catch((e) => setErr(errText(e))); };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-foreground/50">Family invite code</div>
          <div className="font-display font-black text-3xl tracking-[0.2em]" data-testid="family-code">{fam.code}</div>
        </div>
        <button type="button" onClick={copy} data-testid="family-copy-code" className="btn-arcade btn-ghost px-3 py-2 text-xs">
          {copied === "Copied" ? <Check size={14} /> : <Copy size={14} />} {copied || "Copy"}
        </button>
      </div>
      <p className="font-mono text-xs text-foreground/70" data-testid="family-slots">
        {fam.active ? `${fam.slots_left} of ${fam.max_devices} device slots free` : "Plan expired — renew to restore family access"} · your {fam.owner_device_count} signed-in device{fam.owner_device_count === 1 ? "" : "s"} count too.
      </p>
      <ul className="space-y-2" data-testid="family-members">
        {fam.members.length === 0 && <li className="font-mono text-xs text-foreground/50">No one has joined yet. Share the code — they enter it in Settings.</li>}
        {fam.members.map((m) => (
          <li key={m.device_id} className="flex items-center justify-between border-2 border-white/20 p-2" data-testid={`family-member-${m.device_id}`}>
            <span className="font-mono text-sm">{m.label}<span className="block text-[10px] uppercase tracking-widest text-foreground/40">joined {new Date(m.joined_at).toLocaleDateString()}</span></span>
            <button type="button" aria-label="Remove device" data-testid={`family-remove-${m.device_id}`} onClick={() => act(() => removeFamilyMember(did, m.device_id))} className="text-foreground/50 hover:text-brand-danger p-1">
              <Trash2 size={14} />
            </button>
          </li>
        ))}
      </ul>
      <button type="button" data-testid="family-regenerate" onClick={() => act(() => regenerateFamilyCode(did))} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-foreground/60 hover:text-brand-primary font-mono">
        <RefreshCw size={12} /> New code (old one stops working)
      </button>
      {err && <div className="border-2 border-brand-danger p-2 font-mono text-xs text-brand-danger" data-testid="family-owner-error">{err}</div>}
    </div>
  );
};

const JoinView = ({ did, onJoined }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try { await joinFamily(did, code.trim(), deviceLabel()); track("family_joined"); await onJoined(); }
    catch (e2) { setErr(errText(e2)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <p className="font-mono text-sm text-foreground/80">Got a family code? Enter it to unlock Premium on this device.</p>
      <form onSubmit={submit} className="flex gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={6} placeholder="ABC123" data-testid="family-code-input"
          className="flex-1 bg-ink border-2 border-white/30 p-2 font-mono text-sm tracking-[0.3em] uppercase focus:border-brand-primary outline-none" />
        <button type="submit" disabled={busy || code.trim().length !== 6} data-testid="family-join-btn" className="btn-arcade px-3 py-2 text-xs">
          {busy ? <Loader2 size={14} className="animate-spin" /> : "Join"}
        </button>
      </form>
      {err && <div className="border-2 border-brand-danger p-2 font-mono text-xs text-brand-danger" data-testid="family-error">{err}</div>}
      {!isNativeIOS && (
        <Link to="/premium#family" data-testid="family-upsell" className="font-mono text-[11px] uppercase tracking-widest text-brand-primary hover:underline">
          Start a Family plan — A$49.99/yr for 4 devices →
        </Link>
      )}
    </div>
  );
};

export const FamilyCard = () => {
  const { deviceId, refreshPremium } = usePremium();
  const [fam, setFam] = useState(null);

  const load = async () => {
    if (!deviceId) return;
    try { setFam(await getFamily(deviceId)); } catch { setFam({ role: "none" }); }
  };
  const refreshAll = async () => { await load(); await refreshPremium(deviceId); };

  useEffect(() => { load(); }, [deviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section data-testid="settings-family" className="space-y-3">
      <h2 className="font-display font-black uppercase tracking-tight text-xl inline-flex items-center gap-2"><Users size={18} /> Family Plan</h2>
      <div className="brut-card p-4">
        {!fam && <Loader2 size={16} className="animate-spin text-foreground/40" />}
        {fam?.role === "owner" && <OwnerView fam={fam} did={deviceId} onChange={(v) => { setFam(v); refreshPremium(deviceId); }} />}
        {fam?.role === "member" && (
          <div className="space-y-2" data-testid="family-member-view">
            <p className="font-mono text-sm">You're on <strong>{fam.owner}</strong>'s Family plan{fam.active ? "" : " (expired)"}.</p>
            <button type="button" data-testid="family-leave" onClick={() => leaveFamily(deviceId).then(refreshAll).catch(() => load())} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-foreground/60 hover:text-brand-danger font-mono">
              <LogOut size={12} /> Leave family
            </button>
          </div>
        )}
        {fam?.role === "none" && <JoinView did={deviceId} onJoined={refreshAll} />}
      </div>
    </section>
  );
};
